import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Badge } from '../ui/badge';
import { ClockCounterClockwise } from '@phosphor-icons/react';
import { auditLabel } from '../../lib/auditLabels';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadLogs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/audit-logs?limit=100`, {
        withCredentials: true,
      });
      setLogs(data);
    } catch (error) {
      // Silent fail - UI will show empty state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const getActionBadgeVariant = (action) => {
    if (action.includes('create')) return 'default';
    if (action.includes('delete')) return 'destructive';
    if (action.includes('deactivate')) return 'secondary';
    return 'outline';
  };

  if (loading) {
    return <div className="text-center py-8">Loading audit logs...</div>;
  }

  return (
    <div className="space-y-4" data-testid="audit-log">
      <div>
        <p className="text-sm text-muted-foreground">Recent recorded actions, newest first</p>
      </div>

      {logs.length === 0 ? (
        <div className="text-center py-12 border border-border rounded-lg">
          <ClockCounterClockwise className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No audit logs yet</p>
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>Who</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Details</TableHead>
                <TableHead>IP Address</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id} data-testid={`audit-log-${log.id}`}>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                  </TableCell>
                  {/* A member number, resolved by the server at read time.
                      The e-mail this column used to show was stored in every
                      row, which quietly made the audit log a second copy of
                      the membership roll. */}
                  <TableCell className="font-mono text-sm tabular-nums">
                    {log.actor || '—'}
                  </TableCell>
                  <TableCell>
                    {/* Plain words, with the raw value kept on hover for
                        anyone matching it against the server's logs. */}
                    <Badge variant={getActionBadgeVariant(log.action)} title={log.action}>
                      {auditLabel(log.action)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    <span className="text-muted-foreground">{log.target_type}:</span>{' '}
                    <span className="font-mono">{log.target_id.slice(0, 8)}</span>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                    {log.details || '-'}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {log.ip_address || '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default AuditLog;
